import React, { useRef } from 'react';
import {
  InstantSearch,
  HierarchicalMenu,
  RefinementList,
  SortBy,
  Pagination,
  ClearRefinements,
  Highlight,
  Hits,
  HitsPerPage,
  Panel,
  Configure,
  SearchBox,
  Snippet,
  ToggleRefinement,
} from 'react-instantsearch-dom';
import { orderBy } from 'lodash';
import algoliasearch from 'algoliasearch/lite';
import {
  ClearFiltersMobile,
  PriceSlider,
  NoResults,
  ResultsNumberMobile,
  SaveFiltersMobile,
} from './widgets';
import withURLSync from './URLSync';
import { formatNumber } from './utils';
import './Theme.css';
import './App.css';
import './App.mobile.css';
import './widgets/Pagination.css';
import AlgoliaSvg from './AlgoliaSvg';

import nasLogo from './assets/nas-logo.png';

import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';


//Current Setup
// const searchClient = algoliasearch(
//   'EXGMQ1AT3T',
//   '35937a094137cb3ef2779ad674fd9280'
// );

//New setup
const searchClient = algoliasearch(
  '8HO7CPXGQW',
  '077d30783fe587c9eb68954e1ecc75c8'
);

const Hit = ({ hit }) => (
  <a href={ hit.permalink } target="_blank" className="hit">
    <header className="hit-image-container">
      <img src={hit.featuredImage} alt={hit.title} className="hit-image" />
    </header>

    <div className="hit-info-container">
      <h2>
        <Highlight attribute="title" tagName="mark" hit={hit} />
      </h2>
      <p className="hit-description">
        <Snippet attribute="icDesc" tagName="mark" hit={hit} />
      </p>

      <footer>
        <p className="hit-footer">
          <span className="hit-em">£{formatNumber(hit.price)} ex V.A.T</span>{' '}
          <strong></strong>{' '}
        </p>
      </footer>
    </div>
  </a>
);

const App = props => {
  const containerRef = useRef(null);
  const headerRef = useRef(null);
  const [expanded, setExpanded] = React.useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  function openFilters() {
    document.body.classList.add('filtering');
    window.scrollTo(0, 0);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('click', onClick);
  }

  function closeFilters() {
    document.body.classList.remove('filtering');
    containerRef.current.scrollIntoView();
    window.removeEventListener('keyup', onKeyUp);
    window.removeEventListener('click', onClick);
  }

  function onKeyUp(event) {
    if (event.key !== 'Escape') {
      return;
    }

    closeFilters();
  }

  function onClick(event) {
    if (event.target !== headerRef.current) {
      return;
    }

    closeFilters();
  }

  return (
    <InstantSearch
      searchClient={ searchClient } 
      indexName="nippon-parts"
      searchState={props.searchState}
      createURL={props.createURL}
      onSearchStateChange={props.onSearchStateChange}
    >
      <header className="header" ref={headerRef}>
        <p className="header-logo">
          <img src={ nasLogo } className="nas-logo" alt="Nippon Auto Spares Logo" />
        </p>

        <SearchBox
          translations={{
            placeholder: 'Part number, model, manufacturer, engine, …',
          }}
          submit={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 18 18"
            >
              <path d="M18.869 19.162l-5.943-6.484c1.339-1.401 2.075-3.233 2.075-5.178 0-2.003-0.78-3.887-2.197-5.303s-3.3-2.197-5.303-2.197-3.887 0.78-5.303 2.197-2.197 3.3-2.197 5.303 0.78 3.887 2.197 5.303 3.3 2.197 5.303 2.197c1.726 0 3.362-0.579 4.688-1.645l5.943 6.483c0.099 0.108 0.233 0.162 0.369 0.162 0.121 0 0.242-0.043 0.338-0.131 0.204-0.187 0.217-0.503 0.031-0.706zM1 7.5c0-3.584 2.916-6.5 6.5-6.5s6.5 2.916 6.5 6.5-2.916 6.5-6.5 6.5-6.5-2.916-6.5-6.5z" fill="#000000"></path>
            </svg>
          }
        />
      </header>

      <Configure
        attributesToSnippet={['icDesc:10']}
        snippetEllipsisText="…"
        removeWordsIfNoResults="allOptional"
      />

      <main className="container" ref={containerRef}>
        <div className="container-wrapper">
          <section className="container-filters" onKeyUp={onKeyUp}>
            <div className="container-header">
              <h2>Filters</h2>

              <div className="clear-filters" data-layout="desktop">
                <ClearRefinements
                  translations={{
                    reset: (
                      <>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="11"
                          height="11"
                          viewBox="0 0 11 11"
                        >
                          <g fill="none" fillRule="evenodd" opacity=".4">
                            <path d="M0 0h11v11H0z" />
                            <path
                              fill="#000"
                              fillRule="nonzero"
                              d="M8.26 2.75a3.896 3.896 0 1 0 1.102 3.262l.007-.056a.49.49 0 0 1 .485-.456c.253 0 .451.206.437.457 0 0 .012-.109-.006.061a4.813 4.813 0 1 1-1.348-3.887v-.987a.458.458 0 1 1 .917.002v2.062a.459.459 0 0 1-.459.459H7.334a.458.458 0 1 1-.002-.917h.928z"
                            />
                          </g>
                        </svg>
                        Clear filters
                      </>
                    ),
                  }}
                />
              </div>

              <div className="clear-filters" data-layout="mobile">
                <ResultsNumberMobile />
              </div>
            </div>

            <div className="container-body">

            <Accordion expanded={expanded === 'panel1'} onChange={handleChange('panel1')} defaultExpanded="true">
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1bh-content"
                id="panel1bh-header"
              >
                <Typography >Manufacturers</Typography>
                {/* <Typography className={classes.secondaryHeading}>I am an accordion</Typography> */}
              </AccordionSummary>
              <AccordionDetails>
               <RefinementList
                  attribute="manufacturer"
                  searchable={true}
                  showMore={ true }
                  showMoreLimit={ 1000 }
                  transformItems={items => orderBy(items, "label", "asc")}
                  translations={{
                    placeholder: 'Search manufacturers…',
                  }}
                />
              </AccordionDetails>
            </Accordion>
            

              <Accordion expanded={expanded === 'panel2'} onChange={handleChange('panel2')}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel2bh-content"
                id="panel2bh-header"
              >
                <Typography >Models</Typography>
                {/* <Typography className={classes.secondaryHeading}>I am an accordion</Typography> */}
              </AccordionSummary>
              <AccordionDetails>
              <RefinementList
                  attribute="model"
                  searchable={true}
                  showMore={ true }
                  showMoreLimit={ 1000 }
                  transformItems={items => orderBy(items, "label", "asc")}
                  translations={{
                    placeholder: 'Search models…',
                  }}
                />
              </AccordionDetails>
            </Accordion>
              <Accordion expanded={expanded === 'panel3'} onChange={handleChange('panel3')}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel3bh-content"
                id="panel3bh-header"
              >
                <Typography >Fits years</Typography>
                {/* <Typography className={classes.secondaryHeading}>I am an accordion</Typography> */}
              </AccordionSummary>
              <AccordionDetails>
              <RefinementList
                  attribute="longIcYear"
                  searchable={true}
                  showMore={ true }
                  showMoreLimit={ 1000 }
                  transformItems={items => orderBy(items, "label", "asc")}
                  translations={{
                    placeholder: 'Search years…',
                  }}
                />
              </AccordionDetails>
            </Accordion>
              <Accordion expanded={expanded === 'panel4'} onChange={handleChange('panel4')}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel4bh-content"
                id="panel4bh-header"
              >
                <Typography >Part</Typography>
                {/* <Typography className={classes.secondaryHeading}>I am an accordion</Typography> */}
              </AccordionSummary>
              <AccordionDetails>
              <RefinementList
                  attribute="itemName"
                  searchable={true}
                  showMore={ true }
                  showMoreLimit={ 1000 }
                  transformItems={items => orderBy(items, "label", "asc")}
                  translations={{
                    placeholder: 'Search part types…',
                  }}
                />
              </AccordionDetails>
            </Accordion>


              <Panel>
                <ToggleRefinement
                  attribute="hasPartImages"
                  label="Display only items with images"
                  value={true}
                />
              </Panel>

            </div>
          </section>

          <footer className="container-filters-footer" data-layout="mobile">
            <div className="container-filters-footer-button-wrapper">
              <ClearFiltersMobile containerRef={containerRef} />
            </div>

            <div className="container-filters-footer-button-wrapper">
              <SaveFiltersMobile onClick={closeFilters} />
            </div>
          </footer>
        </div>

        <section className="container-results">
          <header className="container-header container-options">
            {/* <SortBy
              className="container-option"
              defaultRefinement="nippon-parts"
              items={[
                {
                  label: 'Sort by featured',
                  value: 'nippon-parts',
                },
                // {
                //   label: 'Price ascending',
                //   value: 'nippon-parts',
                // },
                // {
                //   label: 'Price descending',
                //   value: 'nippon-parts',
                // },
              ]}
            /> */}

            <HitsPerPage
              className="container-option"
              items={[
                {
                  label: '15 hits per page',
                  value: 15,
                },
                {
                  label: '30 hits per page',
                  value: 30,
                },
                {
                  label: '60 hits per page',
                  value: 60,
                },
              ]}
              defaultRefinement={15}
            />
          </header>

          <Hits hitComponent={Hit} />
          <NoResults />

          <footer className="container-footer">
            <Pagination
              padding={2}
              showFirst={false}
              showLast={false}
              translations={{
                previous: (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="10"
                    height="10"
                    viewBox="0 0 10 10"
                  >
                    <g
                      fill="none"
                      fillRule="evenodd"
                      stroke="#000"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.143"
                    >
                      <path d="M9 5H1M5 9L1 5l4-4" />
                    </g>
                  </svg>
                ),
                next: (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="10"
                    height="10"
                    viewBox="0 0 10 10"
                  >
                    <g
                      fill="none"
                      fillRule="evenodd"
                      stroke="#000"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.143"
                    >
                      <path d="M1 5h8M5 9l4-4-4-4" />
                    </g>
                  </svg>
                ),
              }}
            />
          </footer>
        </section>
      </main>

      <aside data-layout="mobile">
        <button
          className="filters-button"
          data-action="open-overlay"
          onClick={openFilters}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewbox="0 0 16 14">
            <path
              d="M15 1H1l5.6 6.3v4.37L9.4 13V7.3z"
              stroke="#fff"
              strokeWidth="1.29"
              fill="none"
              fillRule="evenodd"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Filters
        </button>
      </aside>
    </InstantSearch>
  );
};

export default withURLSync(App);
